/**
 * /onboarding/complete — Stripe post-checkout landing page.
 *
 * Stripe redirects here after a successful checkout.session.completed.
 * The webhook has already (or is about to) sync the subscription status
 * back to LC. We simply show a success state and continue to the import step.
 *
 * Query params from Stripe:
 *   ?checkout=success  — payment succeeded
 *   ?checkout=cancelled — user abandoned checkout (Stripe cancel_url is /onboarding/setup)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

export const Route = createFileRoute("/_auth/onboarding/complete")({
  component: OnboardingCompletePage,
});

function OnboardingCompletePage() {
  const navigate = useNavigate();
  const search = new URLSearchParams(window.location.search);
  const status = search.get("checkout");

  // Auto-advance to import step after a moment
  useEffect(() => {
    const t = setTimeout(() => {
      void navigate({ to: "/imports" });
    }, 3000);
    return () => clearTimeout(t);
  }, [navigate]);

  if (status !== "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-950">
        <div className="max-w-sm text-center p-8">
          <div className="text-4xl mb-4">↩️</div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Payment cancelled
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            No worries — you can set up billing any time from Settings.
          </p>
          <button
            onClick={() => void navigate({ to: "/imports" })}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Continue to imports →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-950">
      <div className="max-w-sm text-center p-8">
        <div className="text-5xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
          You're all set!
        </h2>
        <p className="text-slate-500 dark:text-slate-400 mb-2">
          Payment confirmed. Your account is active.
        </p>
        <p className="text-sm text-slate-400 dark:text-slate-500">
          Taking you to imports in a moment…
        </p>
      </div>
    </div>
  );
}
